import { assoc, init, last, lens, map, over, pipe, prop, sort, split, toLower } from 'ramda';
import uuid from 'uuid-random';

// src = [{id: uuid(), value: 'search target', source: {/* additional data */} }]
const normalizeSource = (src = []) => {
    let result;

    if (!Array.isArray(src) || src.length === 0) {
        return [];
    }

    result = src[0] instanceof Object
        ? src
        : src.map(str => ({
            value: str,
            source: str
        }));

    result = result.map(({ id = uuid(), ...item }) => ({ ...item, id }));

    return result;
};

const calculateTupleOffsets = (tuple) => {
    const offsets = [0];

    tuple.forEach(word =>
        offsets.push(last(offsets) + word.length + 1)
    );

    return init(offsets);
};

const prepareSource = map(
    pipe(
        over(
            lens(prop('value'), assoc('lowerCasedValue')),
            toLower
        ),
        over(
            lens(prop('lowerCasedValue'), assoc('tuple')),
            split(/\s|'(?!s\b)/)
        ),
        over(
            lens(prop('tuple'), assoc('tupleOffsets')),
            calculateTupleOffsets
        )
    )
);

const findWordMatch = (query, tupleWordIndex, tuple) => {
    for (let currentWordIndex = tupleWordIndex; currentWordIndex < tuple.length; currentWordIndex++) {
        if (tuple[currentWordIndex].startsWith(query)) {
            return currentWordIndex;
        }
    }

    return null;
};

const matchInWordsStart = (querySplit, item) => {
    const { tuple, tupleOffsets } = item;
    const matchesStart = [];
    const matchesEnd = [];

    for (let i = 0, tupleWordIndex = 0; i < querySplit.length; i++) {
        const queryWord = querySplit[i];
        const foundWordIndex = findWordMatch(queryWord, tupleWordIndex, tuple);

        if (foundWordIndex === null) {
            return null;
        }

        matchesStart.push(tupleOffsets[foundWordIndex]);
        matchesEnd.push(tupleOffsets[foundWordIndex] + queryWord.length);

        tupleWordIndex = foundWordIndex + 1;
    }

    return {
        ...item,
        matchesStart,
        matchesEnd
    };
};

const matchInLine = (queryLowerCased, item) => {
    const { lowerCasedValue } = item;
    const matchStart = lowerCasedValue.indexOf(queryLowerCased);

    if (matchStart === -1) {
        return null;
    }

    return {
        ...item,
        matchesStart: [matchStart],
        matchesEnd: [matchStart + queryLowerCased.length]
    };
};

// Sorting
// Each query symbol must be matched. So, their position in initial string could be treated as its weight. Skip spaces.
// The rank of each element could be the sum of all weights.
// Sort by rank where items with lower rank come first.

const rangeSum = (a, b) => {
    let result = 0;

    for (let i = a; i < b; i++) {
        result += i;
    }

    return result;
};

const calculateRank = (item) => {
    const { matchesStart, matchesEnd } = item;
    const rank = matchesStart.reduce(
        (memo, matchStart, index) => rangeSum(matchStart, matchesEnd[index]),
        0
    );

    return {
        ...item,
        rank
    };
};

const sortingPredicate = (a, b) => {
    const result = a.rank - b.rank;

    if (result !== 0) {
        return result;
    }

    return a.lowerCasedValue.localeCompare(b.lowerCasedValue);
};

const sortByRelevance = pipe(
    map(calculateRank),
    sort(sortingPredicate)
);

export const searchFlex = (src = []) => {
    const items = pipe(
        normalizeSource,
        prepareSource
    )(src);

    return (query) => {
        if (query == null || query === '') {
            return [];
        }

        const queryLowerCased = query.trim().toLowerCase();
        const querySplit = queryLowerCased.split(' ').filter(Boolean);

        const filteredItems = items
            .map(item => matchInWordsStart(querySplit, item) || matchInLine(queryLowerCased, item))
            .filter(Boolean);

        return sortByRelevance(filteredItems);
    };
};
